import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { useRouter } from 'next/router';
import * as Styled from './Card.styled';
import { getCtaLayout } from './Card.utils';
import Content from './Content/Content';
import { handleNavigate } from '../../../../util/util';
import { ContainerQueryContext } from '../../../../util/ContainerQuery.context';
var DEFAULT_IMAGE_RESOLUTIONS = {
    small: 432,
    medium: 448,
    large: 516,
    xlarge: 670,
    largest: 1520
};
var Card = function (_a) {
    var _b;
    var _c = _a.backgroundColor, backgroundColor = _c === void 0 ? undefined : _c, _d = _a.className, className = _d === void 0 ? '' : _d, _e = _a.ctas, ctas = _e === void 0 ? [] : _e, _f = _a.customBackgroundColor, customBackgroundColor = _f === void 0 ? '' : _f, eyebrow = _a.eyebrow, header = _a.header, headerMaxLineLength = _a.headerMaxLineLength, _g = _a.isTransparent, isTransparent = _g === void 0 ? false : _g, horizontalTextAlignment = _a.horizontalTextAlignment, icon = _a.icon, _h = _a.isMaxHeight, isMaxHeight = _h === void 0 ? false : _h, _j = _a.imageResolutions, imageResolutions = _j === void 0 ? DEFAULT_IMAGE_RESOLUTIONS : _j, responsiveTextSizes = _a.responsiveTextSizes, subheader = _a.subheader, _k = _a.cardTextColor, textColor = _k === void 0 ? 'AUTO' : _k, _l = _a.cardTextPosition, textPosition = _l === void 0 ? 'TOP' : _l, _m = _a.ctaLayout, ctaLayout = _m === void 0 ? 'ONE_ON_CARD' : _m, settings = _a.settings, mobileImage = _a.mobileImage, tabletImage = _a.tabletImage, desktopImage = _a.desktopImage, internalSpace = _a.internalSpace, columnSpan = _a.columnSpan, hoverEffect = _a.hoverEffect, dataLabel = _a.dataLabel;
    var router = useRouter();
    var _o = useState(false), isRevealed = _o[0], setIsRevealed = _o[1];
    var styleQueryMode = useContext(ContainerQueryContext).styleQueryMode;
    var ref = useRef(null);
    var largeScreenImage = desktopImage || tabletImage || mobileImage;
    var mediumScreenImage = tabletImage || mobileImage || desktopImage;
    var smallScreenImage = mobileImage || tabletImage || desktopImage;
    var _p = getCtaLayout(ctaLayout, ctas), cardCtas = _p.cardCtas, overlayCtas = _p.overlayCtas;
    var shouldRenderAnchor = (cardCtas === null || cardCtas === void 0 ? void 0 : cardCtas.length) === 1 && (overlayCtas === null || overlayCtas === void 0 ? void 0 : overlayCtas.length) === 0;
    useEffect(function () {
        var handleGlobalClick = function (e) {
            if (ref.current && !ref.current.contains(e.target))
                setIsRevealed(false);
        };
        var handleEscKeyPress = function (e) {
            if (e.key === 'Escape')
                setIsRevealed(false);
        };
        if (isRevealed) {
            document.addEventListener('click', handleGlobalClick, {
                capture: true
            });
            document.addEventListener('keydown', handleEscKeyPress, {
                capture: true
            });
        }
        return function () {
            document.removeEventListener('click', handleGlobalClick, {
                capture: true
            });
            document.removeEventListener('keypress', handleEscKeyPress, {
                capture: true
            });
        };
    }, [isRevealed]);
    // if cards have overlay, render overlay, otherwise navigate to destination
    var onCardClick = function (e) {
        if ((overlayCtas === null || overlayCtas === void 0 ? void 0 : overlayCtas.length) > 0) {
            if (!isRevealed) {
                setIsRevealed(true);
            }
            if (shouldRenderAnchor) {
                if (cardCtas[0].url) {
                    window.location.href = cardCtas[0].url;
                }
            }
        }
        else {
            e.preventDefault();
            var targetUrl = e.currentTarget.getAttribute('href');
            if (targetUrl)
                handleNavigate(router, targetUrl);
        }
    };
    var contentWrapper = useMemo(function () {
        return function (screenSize, columnSpanForScreenSize, image) {
            return (React.createElement(Content, { backgroundColor: backgroundColor, cardCtas: cardCtas, cardTextColor: textColor, columnSpan: columnSpanForScreenSize, customBackgroundColor: customBackgroundColor, eyebrow: eyebrow, header: header, headerMaxLineLength: headerMaxLineLength, horizontalTextAlignment: horizontalTextAlignment, hoverEffect: hoverEffect, icon: icon, image: image, imageResolutions: imageResolutions, internalSpace: internalSpace, isTransparent: isTransparent || backgroundColor === 'transparent', screenSize: screenSize, settings: settings, subheader: subheader, responsiveTextSizes: responsiveTextSizes, verticalTextPosition: textPosition }));
        };
    }, []);
    var cardBgColorByTextPosition = function (bgColor) {
        return !!smallScreenImage && textPosition !== 'BELOW_IMAGE'
            ? 'transparent'
            : bgColor;
    };
    return (React.createElement(Styled.Card, { "data-label": dataLabel || 'contentCard', backgroundColor: cardBgColorByTextPosition(backgroundColor), customBackgroundColor: cardBgColorByTextPosition(customBackgroundColor), verticalTextPosition: textPosition, textAlignment: horizontalTextAlignment, className: className, onClick: onCardClick, isInteractable: (overlayCtas === null || overlayCtas === void 0 ? void 0 : overlayCtas.length) > 0, as: shouldRenderAnchor ? 'a' : 'div', href: shouldRenderAnchor ? (_b = cardCtas[0].url) !== null && _b !== void 0 ? _b : '' : '', ref: ref, isMaxHeight: isMaxHeight, isFullHeight: !(smallScreenImage === null || smallScreenImage === void 0 ? void 0 : smallScreenImage.file.url) || textPosition === 'BELOW_IMAGE', hoverEffect: hoverEffect, "$styleQueryMode": styleQueryMode },
        React.createElement(Styled.CardContentLarge, { "$styleQueryMode": styleQueryMode }, contentWrapper('LARGE', columnSpan === null || columnSpan === void 0 ? void 0 : columnSpan.desktop, largeScreenImage)),
        React.createElement(Styled.CardContentMedium, { "$styleQueryMode": styleQueryMode }, contentWrapper('MEDIUM', columnSpan === null || columnSpan === void 0 ? void 0 : columnSpan.tablet, mediumScreenImage)),
        React.createElement(Styled.CardContentSmall, { "$styleQueryMode": styleQueryMode }, contentWrapper('SMALL', columnSpan === null || columnSpan === void 0 ? void 0 : columnSpan.mobile, smallScreenImage)),
        overlayCtas && isRevealed && (React.createElement(Styled.CardOverlay, { ctas: overlayCtas, onRequestClose: function () { return setIsRevealed(false); }, isTransparent: backgroundColor === 'transparent', responsiveTextSizes: responsiveTextSizes }))));
};
export default Card;
